import * as React from "react";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Box,
  Grid,
  Button,
  TextField,
  FormControl,
  Autocomplete,
  Typography,
  Card,
  CardMedia,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { AddCircleOutline, RemoveCircleOutline } from "@mui/icons-material";
import { useStyles } from "../../../../Theme/Style";
import { useNotification } from "../../../../Contexts/GlobalNotificationContext";
import CircularProgress from "@mui/material/CircularProgress";

import {
  useGetLabelTypesQuery,
  useGetLabelsTemplatesQuery,
  useSubmitLabelDetailsMutation,
} from "../../../../services/api/labels-templates";

import { useGetLabelDetailsQuery } from "../../../../services/api/production-order";

const LabelsComponent = ({ prodId, templateItems = [] }) => {
  const classes = useStyles();
  const { addNotification } = useNotification();

  console.log("templateItems in LabelsComponent: ", templateItems);

  const [submitLabelDetails] = useSubmitLabelDetailsMutation();

  const { data: labelTypesData, isLoading: typeLoading } =
    useGetLabelTypesQuery();

  const { data: labelsData, isLoading: labelsLoading } =
    useGetLabelsTemplatesQuery();

  const {
    data: labelData = {},
    isLoading: labelDetailsLoading,
  } = useGetLabelDetailsQuery({ id: prodId });

  // Normalise in case API returns { items: [...] } or plain array
  const normalizeList = (src) =>
    Array.isArray(src?.items) ? src.items : Array.isArray(src) ? src : [];

  const labelTypes = normalizeList(labelTypesData);
  const allLabels = normalizeList(labelsData);

  const isEqualById = (o, v) => String(o?.id ?? "") === String(v?.id ?? "");

  // ------------ Local State --------------
  const [rows, setRows] = React.useState([
    { labelType: null, label: null, url: "" },
  ]);

  // ------------ Load from TEMPLATE (if given) --------------
  React.useEffect(() => {
    if (
      !templateItems ||
      !templateItems.length ||
      !labelTypes.length ||
      !allLabels.length
    ) {
      return;
    }

    // New structure: templateItems is an array of { labelTypes: {id, name}, labels: [...] }
    const mapped = templateItems.map((item) => {
      const labelType = labelTypes.find(
        (lt) => String(lt.id) === String(item.labelTypes?.id)
      ) || null;

      // For template, we set the labelType but leave label as null
      // User will select from the available labels for this type
      return {
        labelType,
        label: null,
        url: "",
        // Store available labels for this type for filtering
        availableLabels: item.labels || [],
      };
    });

    setRows(mapped.length ? mapped : [{ labelType: null, label: null, url: "" }]);
  }, [templateItems, labelTypes, allLabels]);

  // ------------ Load existing labels for this production order (only if no template) --------------
  React.useEffect(() => {
    // If we already have template-based rows, don't override them
    if (templateItems && templateItems.length) return;

    if (!labelData?.labels || !labelData.labels.length || !labelTypes.length)
      return;

    const mapped = labelData.labels.map((entry) => {
      const label = entry.label || null;
      const labelType =
        labelTypes.find(
          (t) => String(t.id) === String(label?.label_type_id)
        ) || null;

      return {
        labelType,
        label,
        url: label?.image_url || "",
      };
    });

    setRows(mapped.length ? mapped : [{ labelType: null, label: null, url: "" }]);
  }, [labelData, labelTypes, templateItems]);

  // ------------ Row Actions --------------
  const addRow = () =>
    setRows((prev) => [...prev, { labelType: null, label: null, url: "" }]);

  const deleteRow = (index) =>
    setRows((prev) => {
      const n = [...prev];
      n.splice(index, 1);
      return n.length ? n : [{ labelType: null, label: null, url: "" }];
    });

  const handleChange = (index, field, value) => {
    setRows((prev) => {
      const n = [...prev];
      const row = { ...n[index] };

      if (field === "labelType") {
        row.labelType = value;
        row.label = null; // reset label when type changes
        row.url = "";
      }

      if (field === "label") {
        row.label = value;
        row.url = value?.image_url || "";
      }

      n[index] = row;
      return n;
    });
  };

  const handleSave = async () => {
    // Basic validation
    const isValid = rows.every(
      (r) => r.labelType?.id && r.label?.id && r.url
    );

    if (!isValid) {
      addNotification({
        message: "Please select Label Type, Label and ensure preview for all rows.",
        type: "error",
      });
      return;
    }

    const payload = rows.map((r) => ({
      label_type: r.labelType.id,
      label: r.label.id,
      // label_path from state url or fallback to label.image
      label_path: r.label_path || r.url || r.label?.image || "",
    }));

    try {
      await submitLabelDetails({
        production_order_id: prodId,
        labels: payload,
      }).unwrap();

      addNotification({
        message: "Labels saved successfully!",
        type: "success",
      });
    } catch (e) {
      addNotification({
        message: "Failed to save labels.",
        type: "error",
      });
    }
  };

  // ------------ Loading State --------------
  if (typeLoading || labelsLoading || labelDetailsLoading) {
    return (
      <Box sx={{ textAlign: "center", padding: "20px" }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Accordion className={classes.accordion}>
      <AccordionSummary
        expandIcon={<ExpandMoreIcon sx={{ color: "#fff" }} />}
        sx={{ backgroundColor: "#512879" }}
      >
        <Grid container className={classes.accordionSummaryTextContainer}>
          <Grid item xs={4}>Labels</Grid>
          <Grid item xs={4}>标签</Grid>
        </Grid>
      </AccordionSummary>

      <AccordionDetails sx={{ padding: 0 }}>
        {/* Header */}
        <Box className={classes.tableHeader}>
          <Grid container gap={2}>
            <Grid item xs={4}>Label Type</Grid>
            <Grid item xs={4}>Label</Grid>
            <Grid item xs={3}>Preview</Grid>
            <Grid item xs={1}></Grid>
          </Grid>
        </Box>

        {/* Rows */}
        {rows.map((row, index) => {
          // If row has availableLabels from template, use those; otherwise filter all labels
          const filteredLabels = row.availableLabels && row.availableLabels.length > 0
            ? row.availableLabels
            : row.labelType
            ? allLabels.filter(
                (l) =>
                  String(l.label_type_id) === String(row.labelType?.id)
              )
            : allLabels;

          return (
            <Box key={index} sx={{ marginY: 2 }}>
              <Grid container gap={2} alignItems="center">
                {/* Label Type */}
                <Grid item xs={4}>
                  <FormControl fullWidth variant="standard">
                    <Autocomplete
                      options={labelTypes}
                      value={row.labelType}
                      isOptionEqualToValue={isEqualById}
                      getOptionLabel={(o) => o?.name || ""}
                      onChange={(e, val) =>
                        handleChange(index, "labelType", val)
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          placeholder="Select Label Type"
                          variant="standard"
                        />
                      )}
                    />
                  </FormControl>
                </Grid>

                {/* Label */}
                <Grid item xs={4}>
                  <FormControl fullWidth variant="standard">
                    <Autocomplete
                      options={filteredLabels}
                      value={row.label}
                      isOptionEqualToValue={isEqualById}
                      getOptionLabel={(o) => o?.name || ""}
                      onChange={(e, val) =>
                        handleChange(index, "label", val)
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          placeholder="Select Label"
                          variant="standard"
                        />
                      )}
                    />
                  </FormControl>
                </Grid>

                {/* Preview */}
                <Grid item xs={3}>
                  <Card sx={{ maxHeight: 160 }}>
                    {row.url ? (
                      <CardMedia
                        component="img"
                        image={row.url}
                        height={160}
                        alt={row.label?.name || "Label Preview"}
                      />
                    ) : (
                      <Box
                        sx={{
                          height: 160,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <Typography variant="caption">
                          No Preview
                        </Typography>
                      </Box>
                    )}
                  </Card>
                </Grid>

                {/* Remove */}
                <Grid item xs={1}>
                  <Button onClick={() => deleteRow(index)}>
                    <RemoveCircleOutline />
                  </Button>
                </Grid>
              </Grid>
            </Box>
          );
        })}

        {/* Actions */}
        <Box sx={{ mt: 2, display: "flex", gap: 2 }}>
          <Button variant="outlined" onClick={addRow}>
            <AddCircleOutline sx={{ mr: 1 }} /> Add New Row
          </Button>
          <Button variant="contained" onClick={handleSave}>
            Save
          </Button>
        </Box>
      </AccordionDetails>
    </Accordion>
  );
};

export default LabelsComponent;
